/**
 * DTOs for the organization account console (/account).
 *
 * Every shape here mirrors a real response from the EMS API — the same endpoints
 * the employee-management-system app consumes. Where the API returns Stripe
 * money, the unit is noted on the field: subscription/plan prices are MAJOR
 * units (dollars), invoice amounts are MINOR units (cents), because Stripe
 * objects pass through unconverted.
 */

// ─────────────────────────────── envelope ───────────────────────────────

export interface ApiEnvelope<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

export interface PaginatedEnvelope<T> extends ApiEnvelope<T> {
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ──────────────────────────────── auth ─────────────────────────────────

export interface AccountTokens {
  accessToken: string;
  refreshToken?: string | null;
}

export interface AccountOrganization {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  timezone?: string | null;
  dateFormat?: string | null;
}

export interface AccountRole {
  id: string;
  name: string;
}

export interface AccountEmployee {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  workEmail?: string;
  profilePicture?: string | null;
  organizationId?: string;
}

export interface AccountUser {
  id: string;
  email?: string;
  socialProvider: string | null;
  socialId: string | null;
  profilePicture: string | null;
  isActive: boolean;
  isEmailVerified?: boolean;
  mustChangePassword?: boolean;
  timezone?: string | null;
}

/** One active org membership, as returned by GET /auth/organizations. */
export interface AccountMembership {
  id: string;
  name: string;
  slug: string;
  /** Role name in THAT organization — may differ per membership. */
  role: string;
  isActive: boolean;
  isCurrent?: boolean;
  logoUrl?: string | null;
}

/** Full session payload from POST /auth/login (and /auth/login/verify). */
export interface AccountSession {
  tokens: AccountTokens;
  employee: AccountEmployee;
  organization: AccountOrganization;
  role: AccountRole;
  user: AccountUser;
  organizations?: AccountMembership[];
  requiresOrganizationSelection?: boolean;
}

/** Step-1 login response when the user has an authenticator enrolled. */
export interface AccountMfaChallenge {
  mfaRequired: true;
  pendingToken: string;
  method: 'totp';
}

export type AccountLoginResult = AccountSession | AccountMfaChallenge;

export function isMfaChallenge(result: AccountLoginResult): result is AccountMfaChallenge {
  return (result as AccountMfaChallenge)?.mfaRequired === true;
}

/** What we persist between visits. Tokens live under their own keys. */
export interface AccountContext {
  user: AccountUser;
  employee: AccountEmployee;
  organization: AccountOrganization;
  role: AccountRole;
  organizations?: AccountMembership[];
}

export interface AccountSessionRecord {
  realm: string;
  sessionName: string;
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string | null;
}

export interface MfaStatus {
  enabled: boolean;
  enrolledAt?: string | null;
  recoveryCodesRemaining: number;
}

export interface MfaEnrollment {
  secret: string;
  /** otpauth:// URI — rendered as a QR code. */
  otpauthUrl: string;
}

// ─────────────────────────────── billing ───────────────────────────────

export type BillingCycle = 'monthly' | 'yearly';

export interface SubscriptionPlan {
  id: string;
  name: string;
  dbAlias: string;
  description: string;
  planType: 'base' | 'addon';
  /** Major units (dollars) in the org's billing currency. */
  priceMonthly: number;
  priceYearly: number;
  /** Lowercase ISO code; absent on legacy payloads (= CAD). */
  currency?: string;
  requiresBasePlan: boolean;
  compatibleWithPlans: string[];
  features: string[];
  maxUsers: number;
  maxStorageGb: number;
  isActive: boolean;
}

export interface SubscriptionItem {
  id: string;
  planId: string;
  planName: string;
  planType: 'base' | 'addon';
  dbAlias: string;
  quantity: number;
  pricePerUnit: number;
  totalPrice: number;
  stripeItemId: string | null;
}

/** Present only while the subscription is past_due or suspended. */
export interface SubscriptionDunning {
  status: string;
  inGrace: boolean;
  graceEndsAt: string | null;
  graceDays: number;
}

export type SubscriptionState =
  | 'active'
  | 'trial'
  | 'cancelled'
  | 'past_due'
  | 'suspended';

export interface Subscription {
  id: string;
  organizationId: string;
  status: SubscriptionState;
  billingCycle: BillingCycle;
  currency?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  stripeSubscriptionId: string | null;
  items: SubscriptionItem[];
  /** Major units. */
  totalMonthly: number;
  totalYearly: number;
  nextBillingDate: string;
  metadata?: {
    trial?: { type: string; createdAt: string; durationDays: number };
  };
  dunning?: SubscriptionDunning;
}

/** GET /billing/dashboard — one call for the whole instrument strip. */
export interface BillingDashboard {
  subscription: Subscription | null;
  availableAddons: SubscriptionPlan[];
  currentUsage: {
    users: number;
    storage: number;
    features: string[];
  };
  upcomingInvoice?: {
    /** Major units. */
    amount: number;
    dueDate: string;
    items: Array<{ description: string; amount: number }>;
  };
  paymentMethods: Array<{
    id: string;
    brand: string;
    last4: string;
    isDefault: boolean;
  }>;
  billingHistory: Array<{
    id: string;
    amount: number;
    status: string;
    date: string;
    description: string;
    invoiceUrl?: string;
  }>;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

/** GET /billing/invoices — amounts are MINOR units (cents), Stripe-native. */
export interface TenantInvoice {
  id: string;
  number: string | null;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  created: string;
}

export interface BillingContact {
  /** Effective recipient of billing email. */
  email: string;
  /** Null when billing email goes to the organization owner. */
  overrideEmail: string | null;
}

export interface RetryPaymentOutcome {
  status: 'paid' | 'requires_action' | 'failed' | 'no_open_invoice';
  invoiceId?: string;
  hostedInvoiceUrl?: string | null;
  declineCode?: string;
  message?: string;
}

// ───────────────────────────── organization ────────────────────────────

/** Mirrors `updateOrganizationProfileSchema` on the API — no invented fields. */
export interface OrganizationProfile {
  id: string;
  name: string;
  slug?: string;
  domain?: string | null;
  description?: string | null;
  emailFromAddress?: string | null;
  emailReplyTo?: string | null;
  /** 2-letter uppercase ISO code. */
  country?: string | null;
  businessAddress?: string | null;
  timezone?: string | null;
  website?: string | null;
  industry?: string | null;
  size?: string | null;
  location?: string | null;
  logoKey?: string | null;
  isActive?: boolean;
}

/** The subset a user may PATCH. */
export type OrganizationProfileUpdate = Partial<
  Pick<
    OrganizationProfile,
    | 'name'
    | 'description'
    | 'website'
    | 'industry'
    | 'size'
    | 'location'
    | 'businessAddress'
    | 'country'
    | 'domain'
    | 'emailFromAddress'
    | 'emailReplyTo'
  >
>;

export interface OrganizationMember {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  workEmail?: string;
  profilePicture?: string | null;
  isActive?: boolean;
  createdAt?: string;
  role?: { id: string; name: string } | string | null;
  position?: { id: string; title?: string; name?: string } | null;
  department?: { id: string; name?: string; departmentName?: string } | null;
}

export interface OrganizationRole {
  id: string;
  name: string;
  description?: string | null;
  isSystem?: boolean;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  status?: string;
  roleId?: string;
  role?: { id: string; name: string } | string | null;
  createdAt?: string;
  expiresAt?: string | null;
}
